import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    App as AntdApp,
    Button,
    Form,
    Input,
    Modal,
    Select,
    Table,
} from "antd";
import {
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import {
    FilterActions,
    FilterBar,
    FilterField,
} from "../../components/common/FilterBar";
import {
    buildPaginatedListPagination,
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
} from "../../components/common/PaginatedListTable";
import PageLayout from "../../components/layout/PageLayout/PageLayout";
import { usePermissions } from "../../hooks/usePermissions";
import { getHistoryListPage } from "../../services/historyService";
import { hydrateHistoryListItemsWithSignedUrls } from "../../services/ossService";
import {
    addValidationHistorySamples,
    createValidationDataset,
    deleteValidationDataset,
    deleteValidationSample,
    getValidationDataset,
    getValidationOptions,
    importValidationDatasetSamples,
    listValidationDatasets,
    publishValidationDataset,
    reopenValidationDataset,
    saveValidationAnnotations,
    setValidationDatasetVersionDisabled,
    updateValidationDataset,
} from "../../services/modelValidationService";
import type { HistoryListItem } from "../../types/history";
import type {
    ValidationDataset,
    ValidationDatasetSample,
    ValidationDatasetVersion,
} from "../../types/modelValidation";
import { MODEL_EVALUATE_ACCESS_PERMISSIONS } from "../../utils/permissions";
import { getModelManagementErrorDetail } from "./modelManagement/pageConfig";
import ValidationDatasetAnnotationModal from "./modelValidation/ValidationDatasetAnnotationModal";
import ValidationDatasetDetailModal from "./modelValidation/ValidationDatasetDetailModal";
import ValidationDatasetHistoryPicker from "./modelValidation/ValidationDatasetHistoryPicker";
import ValidationDatasetImportModal from "./modelValidation/ValidationDatasetImportModal";
import type { ValidationDatasetHistoryFilters } from "./modelValidation/ValidationDatasetHistoryPicker";
import {
    buildValidationLabelOptions,
    getCurrentValidationVersion,
} from "./modelValidation/modelValidationUtils";
import {
    buildValidationDatasetColumns,
    buildValidationDatasetSampleColumns,
} from "./modelValidation/validationDatasetColumns";

const PAGE_SIZE = 10;
interface DatasetFormValues {
    description?: string;
    name: string;
}

interface ModelValidationDatasetsPageProps {
    embedded?: boolean;
}

const ModelValidationDatasetsPage: React.FC<
    ModelValidationDatasetsPageProps
> = ({ embedded = false }) => {
    const { message, modal } = AntdApp.useApp();
    const { i18n, t } = useTranslation(["admin", "navigation", "common"]);
    const { hasAnyPermissions } = usePermissions();
    const canEvaluate = hasAnyPermissions(MODEL_EVALUATE_ACCESS_PERMISSIONS);
    const [createForm] = Form.useForm<DatasetFormValues>();
    const [renameForm] = Form.useForm<DatasetFormValues>();
    const [items, setItems] = useState<ValidationDataset[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [keywordDraft, setKeywordDraft] = useState("");
    const [statusFilterDraft, setStatusFilterDraft] = useState<string>();
    const [keyword, setKeyword] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [openCreatedDatasetAfterClose, setOpenCreatedDatasetAfterClose] =
        useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [importCandidates, setImportCandidates] = useState<
        ValidationDataset[]
    >([]);
    const importRequestSequence = useRef(0);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [annotationOpen, setAnnotationOpen] = useState(false);
    const [selectedDataset, setSelectedDataset] =
        useState<ValidationDataset | null>(null);
    const [selectedSample, setSelectedSample] =
        useState<ValidationDatasetSample | null>(null);
    const [historyItems, setHistoryItems] = useState<HistoryListItem[]>([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPageSize, setHistoryPageSize] = useState(PAGE_SIZE);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyFilters, setHistoryFilters] =
        useState<ValidationDatasetHistoryFilters>({});
    const [selectedHistoryIds, setSelectedHistoryIds] = useState<React.Key[]>(
        [],
    );
    const [displayCategories, setDisplayCategories] = useState<
        Awaited<ReturnType<typeof getValidationOptions>>["display_categories"]
    >([]);

    const loadDatasets = useCallback(async () => {
        setLoading(true);
        try {
            const response = await listValidationDatasets({
                keyword: keyword || undefined,
                status: statusFilter,
                limit: pageSize,
                offset: (page - 1) * pageSize,
            });
            setItems(response.items);
            setTotal(response.total);
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.loadDatasetsFailed"),
                    t,
                ),
            );
        } finally {
            setLoading(false);
        }
    }, [keyword, message, page, pageSize, statusFilter, t]);

    useEffect(() => {
        void loadDatasets();
    }, [loadDatasets]);

    const applyFilters = () => {
        setPage(1);
        setKeyword(keywordDraft.trim());
        setStatusFilter(statusFilterDraft);
    };

    const resetFilters = () => {
        setKeywordDraft("");
        setStatusFilterDraft(undefined);
        setPage(1);
        setKeyword("");
        setStatusFilter(undefined);
    };

    useEffect(() => {
        if (canEvaluate) {
            void getValidationOptions()
                .then((response) =>
                    setDisplayCategories(response.display_categories),
                )
                .catch((error) => {
                    setDisplayCategories([]);
                    message.error(
                        getModelManagementErrorDetail(
                            error,
                            t(
                                "admin:modelValidation.messages.loadOptionsFailed",
                            ),
                            t,
                        ),
                    );
                });
        }
    }, [canEvaluate, message, t]);

    const labelOptions = useMemo(
        () => buildValidationLabelOptions(displayCategories, i18n.language),
        [displayCategories, i18n.language],
    );

    const selectedVersion = useMemo<ValidationDatasetVersion | null>(() => {
        if (!selectedDataset) return null;
        return (
            getCurrentValidationVersion(
                selectedDataset.versions,
                selectedDataset.latest_published_version,
            ) || null
        );
    }, [selectedDataset]);

    const updateSelectedDataset = useCallback(
        (dataset: ValidationDataset) => {
            setSelectedDataset(dataset);
            void loadDatasets();
        },
        [loadDatasets],
    );

    const openDatasetEditor = (dataset: ValidationDataset) => {
        setSelectedDataset(dataset);
        renameForm.setFieldsValue({
            name: dataset.name,
            description: dataset.description || undefined,
        });
        setRenameOpen(true);
    };

    const openEditableDataset = (
        dataset: ValidationDataset,
        onReady: (editableDataset: ValidationDataset) => void,
    ) => {
        if (dataset.status === "draft") {
            onReady(dataset);
            return;
        }
        const version = getCurrentValidationVersion(
            dataset.versions,
            dataset.latest_published_version,
        );
        if (!version || dataset.status !== "published" || dataset.is_locked) {
            return;
        }
        modal.confirm({
            cancelText: t("admin:modelValidation.actions.cancel"),
            content: t(
                "admin:modelValidation.confirmations.reopenDatasetDescription",
            ),
            okText: t("admin:modelValidation.actions.continueEditing"),
            maskClosable: false,
            onOk: async () => {
                const reopened = await handleDatasetAction(
                    () =>
                        reopenValidationDataset(
                            dataset.stable_id,
                            version.version_no,
                            version.lock_version,
                        ),
                    "admin:modelValidation.messages.datasetReopened",
                );
                if (reopened) onReady(reopened);
            },
            title: t("admin:modelValidation.confirmations.reopenDatasetTitle"),
        });
    };

    const openDatasetImageManager = async (dataset: ValidationDataset) => {
        setLoading(true);
        try {
            const detail = await getValidationDataset(dataset.stable_id);
            setSelectedDataset(detail);
            setDetailOpen(true);
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.loadDatasetFailed"),
                    t,
                ),
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        const values = await createForm.validateFields();
        setSaving(true);
        try {
            const dataset = await createValidationDataset(values);
            message.success(t("admin:modelValidation.messages.datasetCreated"));
            setOpenCreatedDatasetAfterClose(true);
            setCreateOpen(false);
            createForm.resetFields();
            updateSelectedDataset(dataset);
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.createDatasetFailed"),
                    t,
                ),
            );
        } finally {
            setSaving(false);
        }
    };

    const handleRename = async () => {
        if (!selectedDataset) return;
        const values = await renameForm.validateFields();
        setSaving(true);
        try {
            const dataset = await updateValidationDataset(
                selectedDataset.stable_id,
                values,
            );
            updateSelectedDataset(dataset);
            setRenameOpen(false);
            message.success(t("admin:modelValidation.messages.datasetUpdated"));
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.updateDatasetFailed"),
                    t,
                ),
            );
        } finally {
            setSaving(false);
        }
    };

    const loadHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const response = await getHistoryListPage(
                {
                    mode: "review_export",
                    include_review_preview: true,
                    page: historyPage,
                    page_size: historyPageSize,
                    review_status: "needs_review",
                    sort_by: "review_updated_at",
                    sort_order: "desc",
                    type: "image",
                    filename: historyFilters.filename,
                    device_name: historyFilters.deviceName,
                    start_timestamp: historyFilters.startTimestamp,
                    end_timestamp: historyFilters.endTimestamp,
                },
                true,
            );
            try {
                setHistoryItems(
                    await hydrateHistoryListItemsWithSignedUrls(response.items),
                );
            } catch (error) {
                console.error("Failed to hydrate history thumbnails:", error);
                setHistoryItems(response.items);
            }
            setHistoryTotal(response.total);
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.loadHistoryFailed"),
                    t,
                ),
            );
        } finally {
            setHistoryLoading(false);
        }
    }, [historyFilters, historyPage, historyPageSize, message, t]);

    useEffect(() => {
        if (historyOpen) void loadHistory();
    }, [historyOpen, loadHistory]);

    const handleAddHistory = async () => {
        if (!selectedDataset || !selectedVersion) return;
        setSaving(true);
        try {
            const dataset = await addValidationHistorySamples(
                selectedDataset.stable_id,
                selectedVersion.version_no,
                selectedHistoryIds.map(String),
                selectedVersion.lock_version,
            );
            updateSelectedDataset(dataset);
            setHistoryOpen(false);
            setSelectedHistoryIds([]);
            message.success(t("admin:modelValidation.messages.samplesAdded"));
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.addSamplesFailed"),
                    t,
                ),
            );
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAnnotations = async (payload: {
        annotations: Array<{
            annotation_id: string;
            display_category_key: string;
            bbox: { x1: number; y1: number; x2: number; y2: number };
        }>;
        reviewed: boolean;
    }) => {
        if (
            !selectedDataset ||
            !selectedVersion ||
            !selectedSample ||
            selectedSample.id == null
        )
            return;
        setSaving(true);
        try {
            const dataset = await saveValidationAnnotations(
                selectedDataset.stable_id,
                selectedVersion.version_no,
                selectedSample.id,
                {
                    lockVersion: selectedVersion.lock_version,
                    reviewed: payload.reviewed,
                    annotations: payload.annotations,
                },
            );
            updateSelectedDataset(dataset);
            setAnnotationOpen(false);
            message.success(
                t("admin:modelValidation.messages.annotationsSaved"),
            );
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.saveAnnotationsFailed"),
                    t,
                ),
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDatasetAction = async (
        action: () => Promise<ValidationDataset>,
        successMessageKey?: string,
    ): Promise<ValidationDataset | null> => {
        setSaving(true);
        try {
            const dataset = await action();
            updateSelectedDataset(dataset);
            if (successMessageKey) {
                message.success(t(successMessageKey));
            }
            return dataset;
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.operationFailed"),
                    t,
                ),
            );
            return null;
        } finally {
            setSaving(false);
        }
    };

    const loadImportCandidates = useCallback(
        async (searchKeyword = "") => {
            if (!selectedDataset) return;
            const requestSequence = ++importRequestSequence.current;
            setImportLoading(true);
            setImportCandidates([]);
            try {
                const response = await listValidationDatasets({
                    keyword: searchKeyword || undefined,
                    limit: 100,
                    offset: 0,
                });
                if (requestSequence !== importRequestSequence.current) return;
                setImportCandidates(
                    response.items.filter((candidate) => {
                        if (candidate.stable_id === selectedDataset.stable_id) {
                            return false;
                        }
                        const candidateVersion = getCurrentValidationVersion(
                            candidate.versions,
                            candidate.latest_published_version,
                        );
                        return (
                            candidate.is_read_only ||
                            (candidateVersion?.sample_count || 0) > 0
                        );
                    }),
                );
            } catch (error) {
                if (requestSequence !== importRequestSequence.current) return;
                setImportCandidates([]);
                message.error(
                    getModelManagementErrorDetail(
                        error,
                        t(
                            "admin:modelValidation.messages.loadImportDatasetsFailed",
                        ),
                        t,
                    ),
                );
            } finally {
                if (requestSequence === importRequestSequence.current) {
                    setImportLoading(false);
                }
            }
        },
        [message, selectedDataset, t],
    );

    const closeDatasetImport = () => {
        importRequestSequence.current += 1;
        setImportLoading(false);
        setImportOpen(false);
    };

    const openDatasetImport = () => {
        if (!selectedDataset || !selectedVersion) return;
        setImportCandidates([]);
        setImportOpen(true);
        void loadImportCandidates();
    };

    const handleImportDataset = async (sourceDatasetStableId: string) => {
        if (!selectedDataset || !selectedVersion) return;
        setSaving(true);
        try {
            const result = await importValidationDatasetSamples(
                selectedDataset.stable_id,
                selectedVersion.version_no,
                sourceDatasetStableId,
                selectedVersion.lock_version,
            );
            updateSelectedDataset(result.dataset);
            closeDatasetImport();
            message.success(
                t("admin:modelValidation.messages.datasetImagesImported", {
                    imported: result.imported_count,
                    skipped: result.skipped_count,
                }),
            );
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        "admin:modelValidation.messages.importDatasetImagesFailed",
                    ),
                    t,
                ),
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDatasetStatusChange = (
        dataset: ValidationDataset,
        disabled: boolean,
    ) => {
        const version = getCurrentValidationVersion(
            dataset.versions,
            dataset.latest_published_version,
        );
        if (!version) return;
        modal.confirm({
            cancelText: t("admin:modelValidation.actions.cancel"),
            content: t(
                `admin:modelValidation.confirmations.${disabled ? "disableDatasetDescription" : "enableDatasetDescription"}`,
            ),
            okButtonProps: { danger: disabled },
            okText: t(
                `admin:modelValidation.actions.${disabled ? "disable" : "restore"}`,
            ),
            maskClosable: false,
            onOk: () =>
                handleDatasetAction(
                    () =>
                        setValidationDatasetVersionDisabled(
                            dataset.stable_id,
                            version.version_no,
                            version.lock_version,
                            disabled,
                        ),
                    `admin:modelValidation.messages.${disabled ? "datasetDisabled" : "datasetEnabled"}`,
                ),
            title: t(
                `admin:modelValidation.confirmations.${disabled ? "disableDatasetTitle" : "enableDatasetTitle"}`,
            ),
        });
    };

    const handleDeleteDataset = async (dataset: ValidationDataset) => {
        setSaving(true);
        try {
            await deleteValidationDataset(dataset.stable_id);
            if (selectedDataset?.stable_id === dataset.stable_id) {
                setSelectedDataset(null);
                setDetailOpen(false);
            }
            await loadDatasets();
            message.success(t("admin:modelValidation.messages.datasetDeleted"));
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.deleteDatasetFailed"),
                    t,
                ),
            );
        } finally {
            setSaving(false);
        }
    };

    const datasetColumns = buildValidationDatasetColumns({
        t,
        canEvaluate,
        onDelete: handleDeleteDataset,
        onEdit: openDatasetEditor,
        onManageImages: (dataset) => {
            if (
                dataset.status === "published" &&
                !dataset.is_read_only &&
                !dataset.is_locked
            ) {
                openEditableDataset(dataset, (editableDataset) => {
                    setSelectedDataset(editableDataset);
                    setDetailOpen(true);
                });
                return;
            }
            void openDatasetImageManager(dataset);
        },
        onSetDisabled: handleDatasetStatusChange,
    });
    const sampleColumns = buildValidationDatasetSampleColumns({
        canEvaluate,
        onAnnotate: (sample) => {
            setSelectedSample(sample);
            setAnnotationOpen(true);
        },
        onDelete: (sample) => {
            if (!selectedDataset || !selectedVersion || sample.id == null) {
                return Promise.resolve();
            }
            const sampleId = sample.id;
            return handleDatasetAction(() =>
                deleteValidationSample(
                    selectedDataset.stable_id,
                    selectedVersion.version_no,
                    sampleId,
                    selectedVersion.lock_version,
                ),
            ).then(() => undefined);
        },
        selectedVersion,
        t,
    });

    return (
        <PageLayout
            embedded={embedded}
            title={t("admin:modelValidation.datasets.title")}
            breadcrumb={[
                { title: t("navigation:breadcrumb.modelManagement") },
                { title: t("admin:modelValidation.datasets.title") },
            ]}
        >
            <FilterBar>
                <FilterField label={t("admin:modelValidation.filters.name")}>
                    <Input
                        allowClear
                        onChange={(event) =>
                            setKeywordDraft(event.target.value)
                        }
                        onPressEnter={applyFilters}
                        placeholder={t(
                            "admin:modelValidation.datasets.searchPlaceholder",
                        )}
                        value={keywordDraft}
                    />
                </FilterField>
                <FilterField label={t("admin:modelValidation.filters.status")}>
                    <Select
                        allowClear
                        onChange={setStatusFilterDraft}
                        options={["draft", "published", "disabled"].map(
                            (value) => ({
                                value,
                                label: t(
                                    `admin:modelValidation.status.${value}`,
                                ),
                            }),
                        )}
                        value={statusFilterDraft}
                    />
                </FilterField>
                <FilterActions align="left">
                    <Button
                        aria-label={t("common:buttons.search")}
                        icon={<SearchOutlined />}
                        onClick={applyFilters}
                        type="primary"
                    >
                        {t("common:buttons.search")}
                    </Button>
                    <Button
                        aria-label={t("common:buttons.reset")}
                        icon={<ReloadOutlined />}
                        onClick={resetFilters}
                    >
                        {t("common:buttons.reset")}
                    </Button>
                </FilterActions>
            </FilterBar>
            <PaginatedListContainer>
                <PaginatedListToolbar>
                    {canEvaluate ? (
                        <>
                            <Button
                                aria-label={t("common:buttons.add")}
                                icon={<PlusOutlined />}
                                onClick={() => setCreateOpen(true)}
                                type="primary"
                            >
                                {t("common:buttons.add")}
                            </Button>
                        </>
                    ) : null}
                </PaginatedListToolbar>
                <PaginatedListCard>
                    <Table
                        columns={datasetColumns}
                        dataSource={items}
                        loading={loading}
                        pagination={buildPaginatedListPagination({
                            current: page,
                            pageSize,
                            total,
                            showTotal: (count) =>
                                t("admin:modelValidation.pagination.totalRecords", {
                                    total: count,
                                }),
                            onChange: (nextPage, nextPageSize) => {
                                const resolvedPageSize = nextPageSize ?? pageSize;
                                setPage(
                                    resolvedPageSize !== pageSize ? 1 : nextPage,
                                );
                                setPageSize(resolvedPageSize);
                            },
                        })}
                        rowKey="stable_id"
                        scroll={{ x: 930 }}
                        size={PAGINATED_LIST_TABLE_SIZE}
                    />
                </PaginatedListCard>
            </PaginatedListContainer>

            <Modal
                afterClose={() => {
                    if (!openCreatedDatasetAfterClose) return;
                    setOpenCreatedDatasetAfterClose(false);
                    setDetailOpen(true);
                }}
                cancelButtonProps={{ "aria-label": t("common:buttons.cancel") }}
                confirmLoading={saving}
                cancelText={t("common:buttons.cancel")}
                maskClosable={false}
                onCancel={() => {
                    setOpenCreatedDatasetAfterClose(false);
                    setCreateOpen(false);
                }}
                onOk={() => void handleCreate()}
                okButtonProps={{ "aria-label": t("common:buttons.save") }}
                okText={t("common:buttons.save")}
                open={createOpen}
                title={t("admin:modelValidation.actions.createDataset")}
            >
                <Form form={createForm} layout="vertical">
                    <Form.Item
                        label={t("admin:modelValidation.fields.name")}
                        name="name"
                        rules={[{ required: true, max: 128 }]}
                    >
                        <Input maxLength={128} />
                    </Form.Item>
                    <Form.Item
                        label={t("admin:modelValidation.fields.description")}
                        name="description"
                        rules={[{ max: 1000 }]}
                    >
                        <Input.TextArea
                            autoSize={{ minRows: 3, maxRows: 6 }}
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                cancelButtonProps={{ "aria-label": t("common:buttons.cancel") }}
                cancelText={t("common:buttons.cancel")}
                confirmLoading={saving}
                maskClosable={false}
                onCancel={() => setRenameOpen(false)}
                onOk={() => void handleRename()}
                okButtonProps={{ "aria-label": t("common:buttons.save") }}
                okText={t("common:buttons.save")}
                open={renameOpen}
                title={t("admin:modelValidation.datasets.renameTitle")}
            >
                <Form form={renameForm} layout="vertical">
                    <Form.Item
                        label={t("admin:modelValidation.fields.name")}
                        name="name"
                        rules={[{ required: true, max: 128 }]}
                    >
                        <Input maxLength={128} />
                    </Form.Item>
                    <Form.Item
                        label={t("admin:modelValidation.fields.description")}
                        name="description"
                        rules={[{ max: 1000 }]}
                    >
                        <Input.TextArea
                            autoSize={{ minRows: 3, maxRows: 6 }}
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <ValidationDatasetDetailModal
                canEvaluate={canEvaluate}
                dataset={detailOpen ? selectedDataset : null}
                onClose={() => setDetailOpen(false)}
                onOpenDatasetImport={() => void openDatasetImport()}
                onOpenHistory={() => {
                    setHistoryFilters({});
                    setHistoryPage(1);
                    setHistoryPageSize(PAGE_SIZE);
                    setSelectedHistoryIds([]);
                    setHistoryOpen(true);
                }}
                onPublish={async () => {
                    if (!selectedDataset || !selectedVersion) {
                        return;
                    }
                    const publishedDataset = await handleDatasetAction(
                        () =>
                            publishValidationDataset(
                                selectedDataset.stable_id,
                                selectedVersion.version_no,
                                selectedVersion.lock_version,
                            ),
                        "admin:modelValidation.messages.datasetEnabled",
                    );
                    if (publishedDataset) {
                        setDetailOpen(false);
                    }
                }}
                sampleColumns={sampleColumns}
                saving={saving}
                version={selectedVersion}
            />

            <ValidationDatasetImportModal
                datasets={importCandidates}
                loading={importLoading}
                onClose={closeDatasetImport}
                onConfirm={(sourceDatasetStableId) =>
                    void handleImportDataset(sourceDatasetStableId)
                }
                onSearch={(searchKeyword) =>
                    void loadImportCandidates(searchKeyword)
                }
                open={importOpen}
                saving={saving}
            />

            <ValidationDatasetHistoryPicker
                excludedIds={
                    selectedVersion?.samples
                        .map((sample) => sample.history_id)
                        .filter((id): id is string => Boolean(id)) || []
                }
                items={historyItems}
                loading={historyLoading}
                maxSelectable={Math.max(
                    0,
                    500 - (selectedVersion?.sample_count || 0),
                )}
                onClose={() => {
                    setHistoryOpen(false);
                    setHistoryFilters({});
                    setHistoryPage(1);
                    setHistoryPageSize(PAGE_SIZE);
                    setSelectedHistoryIds([]);
                }}
                onConfirm={() => void handleAddHistory()}
                onPageChange={(nextPage, nextPageSize) => {
                    setHistoryPageSize(nextPageSize);
                    setHistoryPage(
                        nextPageSize !== historyPageSize ? 1 : nextPage,
                    );
                }}
                onSearch={(filters) => {
                    setHistoryPage(1);
                    setHistoryFilters(filters);
                }}
                onSelectionChange={setSelectedHistoryIds}
                open={historyOpen}
                page={historyPage}
                pageSize={historyPageSize}
                saving={saving}
                selectedIds={selectedHistoryIds}
                total={historyTotal}
            />

            <ValidationDatasetAnnotationModal
                labelOptions={labelOptions}
                loading={saving}
                onClose={() => setAnnotationOpen(false)}
                onSave={(payload) => void handleSaveAnnotations(payload)}
                open={annotationOpen}
                sample={selectedSample}
                version={selectedVersion}
            />
        </PageLayout>
    );
};

export default ModelValidationDatasetsPage;
